import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { authUtils } from "@/utils/auth";
import {
  Plus, Trash2, Clock, CalendarIcon, RefreshCw, X, Lock, Unlock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Room display names
const ROOM_NAMES = {
  "recording_studio": "Recording Studio",
  "production_room": "Production Room",
  "dj_room": "DJ Room",
  "photography_studio": "Photography Studio",
  "music_lesson_room": "Music Lesson Room"
};

// Format time in 12-hour format
const formatTime12h = (hour) => {
  const h = hour % 24;
  if (h === 0) return "12:00 AM";
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return "12:00 PM";
  return `${h - 12}:00 PM`;
};

// DJ Room Afterdark hours: 24-27 = 12AM-4AM after the selected date
const AFTERDARK_HOURS = [24, 25, 26, 27];
const isAfterdarkHour = (hour) => hour >= 24;

// Create Block Modal
function CreateBlockModal({ onClose, onCreated, availableRooms }) {
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHours, setSelectedHours] = useState([]);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Generate hours 0-23
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const toggleHour = (hour) => {
    setSelectedHours(prev => 
      prev.includes(hour) 
        ? prev.filter(h => h !== hour)
        : [...prev, hour].sort((a, b) => a - b)
    );
  };

  const handleCreate = async () => {
    if (!selectedRoom) {
      toast.error("Please select a room");
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (selectedHours.length === 0) {
      toast.error("Please select at least one hour to block");
      return;
    }

    setLoading(true);
    try {
      const token = authUtils.getToken();
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      await axios.post(
        `${API}/admin/blocked-slots`,
        {
          room_type: selectedRoom,
          date: dateStr,
          hours: selectedHours,
          reason: reason || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Time slots blocked successfully!");
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block slots");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-white">Block Time Slots</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Room Selection */}
          <div>
            <label className="text-white/60 text-sm block mb-2">Select Room *</label>
            <Select value={selectedRoom} onValueChange={(r) => { setSelectedRoom(r); if (r !== "dj_room") setSelectedHours(h => h.filter(x => x < 24)); }}>
              <SelectTrigger className="bg-dream-bg border-dream-border text-white">
                <SelectValue placeholder="Choose a room..." />
              </SelectTrigger>
              <SelectContent className="bg-dream-surface border-dream-border">
                {availableRooms.map((room) => (
                  <SelectItem key={room} value={room} className="text-white">
                    {ROOM_NAMES[room] || room}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Selection */}
          <div>
            <label className="text-white/60 text-sm block mb-2">Select Date *</label>
            <div className="bg-dream-surface rounded-lg p-4 inline-block">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-md"
              />
            </div>
            {selectedDate && (
              <p className="text-dream-violet text-sm mt-2">
                Selected: {selectedDate.toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Hours Selection */}
          <div>
            <label className="text-white/60 text-sm block mb-2">
              Select Hours to Block * ({selectedHours.length} selected)
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {hours.map((hour) => (
                <button
                  key={hour}
                  type="button"
                  onClick={() => toggleHour(hour)}
                  className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                    selectedHours.includes(hour)
                      ? "border-red-500 bg-red-500/20 text-red-400"
                      : "border-dream-border bg-dream-surface text-white/60 hover:border-red-500/50"
                  }`}
                >
                  {formatTime12h(hour)}
                </button>
              ))}
            </div>
          </div>

          {selectedRoom === "dj_room" && (
            <div className="p-4 rounded-xl border border-purple-500/40 bg-purple-900/20" data-testid="afterdark-block-section">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <p className="text-purple-300 text-sm font-semibold">DMRG Afterdark (12AM – 4AM)</p>
                  <p className="text-white/50 text-xs">After midnight on the night of {selectedDate ? selectedDate.toLocaleDateString() : "the selected date"}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedHours(prev => [...new Set([...prev, ...AFTERDARK_HOURS])].sort((a, b) => a - b))}
                  className="px-3 py-1.5 rounded-full bg-purple-500 hover:bg-purple-400 text-white text-xs font-bold"
                  data-testid="block-all-afterdark-btn"
                >
                  Block all Afterdark
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {AFTERDARK_HOURS.map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => toggleHour(hour)}
                    className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                      selectedHours.includes(hour)
                        ? "border-red-500 bg-red-500/20 text-red-400"
                        : "border-purple-500/40 bg-dream-surface text-white/70 hover:border-red-500/50"
                    }`}
                    data-testid={`afterdark-hour-${hour}`}
                  >
                    {formatTime12h(hour)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reason (optional) */}
          <div>
            <label className="text-white/60 text-sm block mb-2">Reason (optional)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Maintenance, Private event..."
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={onClose} variant="outline" className="flex-1 border-dream-border text-white">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={loading}
            className="flex-1 bg-red-600 text-white hover:bg-red-700"
          >
            {loading ? "Blocking..." : "Block Selected Hours"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function BlockedSlotsManagement({ user }) {
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterRoom, setFilterRoom] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = authUtils.getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${API}/admin/blocked-slots`, { headers });
      setBlockedSlots(res.data.blocked_slots || []);
      setAvailableRooms(res.data.available_rooms || []);
    } catch (error) {
      console.error("Error fetching blocked slots:", error);
      toast.error("Failed to load blocked slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (slotId) => {
    if (!window.confirm("Are you sure you want to unblock these time slots?")) return;

    try {
      const token = authUtils.getToken();
      await axios.delete(`${API}/admin/blocked-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Time slots unblocked");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unblock slots");
    }
  };

  const handleRemoveHour = async (slotId, hourToRemove, currentHours) => {
    const newHours = currentHours.filter(h => h !== hourToRemove);
    
    try {
      const token = authUtils.getToken();
      await axios.put(
        `${API}/admin/blocked-slots/${slotId}/hours`,
        newHours,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Hour unblocked");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  // Filter blocked slots
  const filteredSlots = filterRoom === "all" 
    ? blockedSlots 
    : blockedSlots.filter(s => s.room_type === filterRoom);

  // Group by date for display
  const groupedByDate = filteredSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-dream-violet animate-spin" />
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-white">Availability Management</h2>
          <p className="text-white/50 text-sm">Block time slots when unavailable</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-red-600 text-white hover:bg-red-700"
        >
          <Lock className="w-4 h-4 mr-2" />
          Block Time Slots
        </Button>
      </div>

      {/* Filter by Room */}
      <div className="mb-6">
        <Select value={filterRoom} onValueChange={setFilterRoom}>
          <SelectTrigger className="w-full sm:w-64 bg-dream-bg border-dream-border text-white">
            <SelectValue placeholder="Filter by room..." />
          </SelectTrigger>
          <SelectContent className="bg-dream-surface border-dream-border">
            <SelectItem value="all" className="text-white">All Rooms</SelectItem>
            {availableRooms.map((room) => (
              <SelectItem key={room} value={room} className="text-white">
                {ROOM_NAMES[room] || room}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Blocked Slots List */}
      {Object.keys(groupedByDate).length === 0 ? (
        <div className="text-center py-12">
          <Unlock className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No blocked time slots</p>
          <p className="text-white/30 text-sm">All times are currently available for booking</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDate)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, slots]) => (
              <div key={date} className="bg-dream-surface rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarIcon className="w-5 h-5 text-dream-violet" />
                  <h3 className="font-heading font-bold text-white">
                    {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {slots.map((slot) => (
                    <div key={slot.id} className="bg-dream-bg rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-dream-violet/20 text-dream-violet text-xs rounded">
                            {ROOM_NAMES[slot.room_type] || slot.room_type}
                          </span>
                          {slot.reason && (
                            <span className="text-white/40 text-xs">• {slot.reason}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDelete(slot.id)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                          title="Unblock all hours"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {slot.hours.sort((a, b) => a - b).map((hour) => (
                          <div 
                            key={hour}
                            className="group flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm"
                          >
                            <Clock className="w-3 h-3" />
                            {formatTime12h(hour)}{isAfterdarkHour(hour) && <span className="ml-1 text-[10px] text-purple-300">(Afterdark)</span>}
                            <button
                              onClick={() => handleRemoveHour(slot.id, hour, slot.hours)}
                              className="ml-1 opacity-0 group-hover:opacity-100 hover:text-red-300 transition-opacity"
                              title="Unblock this hour"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-white/30 text-xs mt-2">
                        Blocked by {slot.created_by} on {new Date(slot.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateBlockModal
          onClose={() => setShowCreateModal(false)}
          onCreated={fetchData}
          availableRooms={availableRooms}
        />
      )}
    </div>
  );
}
